import React from 'react';
import { motion } from 'framer-motion';
import {
  HelpOutline,
  CheckCircleOutline,
  CancelOutlined,
} from '@mui/icons-material';

import Button, { ButtonColorTheme } from '../../formElements/Button';
import Modal from '../Modal';

type Props = {
  message: string;
  onConfirm: () => void;
  onCancel?: () => void;
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: keyof ButtonColorTheme;
  type?: 'question' | 'warning' | 'danger';
};

function Confirm({
  message,
  onConfirm,
  onCancel,
  isOpen,
  onClose,
  title = 'Potwierdzenie',
  confirmText = 'Tak',
  cancelText = 'Nie',
  confirmColor = 'primary',
  type = 'question',
}: Props) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 },
    },
  };

  // Konfiguracja wyglądu w zależności od typu
  const getTypeConfig = () => {
    switch (type) {
      case 'warning':
        return {
          icon: <HelpOutline fontSize="large" className="text-amber-500" />,
          bgColor: 'bg-amber-50',
        };
      case 'danger':
        return {
          icon: <CancelOutlined fontSize="large" className="text-red-500" />,
          bgColor: 'bg-red-50',
        };
      default:
        return {
          icon: <HelpOutline fontSize="large" className="text-primary" />,
          bgColor: 'bg-primary/10',
        };
    }
  };

  const typeConfig = getTypeConfig();

  return (
    <Modal show={isOpen} onClose={onClose} size="sm">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="sm:min-w-[350px]"
      >
        {/* Nagłówek */}
        <motion.div
          variants={itemVariants}
          className="flex flex-col items-center justify-center gap-3 mb-6"
        >
          <div className={`rounded-full p-4 ${typeConfig.bgColor}`}>
            {typeConfig.icon}
          </div>
          <h2 className="text-xl font-medium text-gray-900">{title}</h2>
        </motion.div>

        {/* Treść */}
        <motion.div variants={itemVariants} className="text-center mb-8">
          <p className="text-gray-600">{message}</p>
        </motion.div>

        {/* Przyciski akcji */}
        <motion.div
          variants={itemVariants}
          className="flex sm:flex-row flex-col-reverse gap-3"
        >
          <motion.div
            className="flex-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              onClick={() => {
                if (onCancel) {
                  onCancel();
                }
                onClose();
              }}
              size="small"
              colorTheme="outline"
              className="w-full py-2.5"
            >
              <CancelOutlined fontSize="small" />
              <span>{cancelText}</span>
            </Button>
          </motion.div>

          <motion.div
            className="flex-1"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Button
              onClick={() => {
                onConfirm();
                onClose();
              }}
              colorTheme={confirmColor}
              size="small"
              className="w-full py-2.5"
            >
              <CheckCircleOutline fontSize="small" />
              <span>{confirmText}</span>
            </Button>
          </motion.div>
        </motion.div>
      </motion.div>
    </Modal>
  );
}

export default Confirm;
